import { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, X, Plus, Save, Flame, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_LENGTHS = [7, 14, 21, 30];

/**
 * Admin panel for editing the per-plan track lists for the Inner Shift challenges.
 * Lets admin pick & order tracks for each of 7/14/21/30-day plans.
 */
export default function ChallengeAdminPanel({ authFetch, allTracks }) {
  const [config, setConfig] = useState(null);
  const [activeLength, setActiveLength] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/inner-shift`);
      const data = await res.json();
      setConfig(data);
    } catch {
      toast.error('Failed to load challenge config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>;
  }
  if (!config) return null;

  const planIds = (config.plans?.[String(activeLength)] || []).slice();
  const trackById = Object.fromEntries(allTracks.map(t => [t.id, t]));
  const inPlan = new Set(planIds);
  const remaining = activeLength - planIds.length;

  // Available tracks (not in plan, matching search)
  const q = search.trim().toLowerCase();
  const candidates = allTracks
    .filter(t => !inPlan.has(t.id))
    .filter(t => !q || (t.title || '').toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q));

  const updatePlan = (newIds) => {
    setConfig(c => ({ ...c, plans: { ...c.plans, [String(activeLength)]: newIds } }));
  };

  const addTrack = (id) => {
    if (inPlan.has(id)) return;
    if (planIds.length >= activeLength) {
      toast.warning(`The ${activeLength}-day plan is full`);
      return;
    }
    updatePlan([...planIds, id]);
  };
  const removeTrack = (id) => updatePlan(planIds.filter(x => x !== id));
  const move = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= planIds.length) return;
    const arr = planIds.slice();
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    updatePlan(arr);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/inner-shift`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plans: config.plans,
          reward_track_id: config.reward_track_id || '',
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setConfig(data);
      toast.success('Challenge tracks saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="challenge-admin-panel">
      {/* Plan length tabs */}
      <div className="flex flex-wrap gap-2">
        {PLAN_LENGTHS.map(len => {
          const filled = (config.plans?.[String(len)] || []).length;
          const isActive = activeLength === len;
          return (
            <button
              key={len}
              onClick={() => setActiveLength(len)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                isActive
                  ? 'bg-amber-400 text-zinc-950 border-amber-400'
                  : 'bg-zinc-900 text-zinc-300 border-white/5 hover:border-white/15'
              }`}
              data-testid={`plan-tab-${len}`}
            >
              <Flame className="w-3.5 h-3.5" />
              {len}-day
              <span className={`text-xs ${isActive ? 'text-zinc-700' : 'text-zinc-500'}`}>
                {filled}/{len}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-zinc-950 font-medium text-sm disabled:opacity-60"
          data-testid="challenge-save-btn"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <p className="text-xs text-zinc-500">
        Editing <span className="text-amber-400">{activeLength}-day</span> challenge — {planIds.length}/{activeLength} tracks selected
        {remaining > 0 && ` · ${remaining} more needed`}
      </p>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Selected tracks (ordered) */}
        <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Day-by-Day Order</p>
          {planIds.length === 0 ? (
            <p className="text-sm text-zinc-600 text-center py-8">No tracks yet. Add from the right.</p>
          ) : (
            <div className="space-y-1.5">
              {planIds.map((id, idx) => {
                const t = trackById[id];
                if (!t) {
                  return (
                    <div key={id + idx} className="flex items-center gap-2 p-2 rounded-md bg-red-500/10 border border-red-500/20 text-xs text-red-300">
                      Day {idx + 1}: track missing — <button onClick={() => removeTrack(id)} className="underline">remove</button>
                    </div>
                  );
                }
                return (
                  <div key={id + idx} className="flex items-center gap-2 p-2 rounded-md bg-zinc-800/50 border border-white/5" data-testid={`plan-track-${idx}`}>
                    <span className="text-[10px] text-zinc-500 w-7 text-right tabular-nums">D{idx + 1}</span>
                    <div className="w-8 h-8 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
                      {t.image_url && <img src={t.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{t.title}</p>
                      <p className="text-[10px] text-zinc-500 capitalize truncate">{t.category}</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-500 hover:text-amber-400 disabled:opacity-20" title="Move up">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => move(idx, 1)} disabled={idx === planIds.length - 1} className="p-1 text-zinc-500 hover:text-amber-400 disabled:opacity-20" title="Move down">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeTrack(id)} className="p-1 text-zinc-500 hover:text-red-400" title="Remove">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available tracks */}
        <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Available Tracks</p>
            <div className="relative">
              <Search className="w-3 h-3 text-zinc-600 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="bg-zinc-950/60 border border-white/5 rounded-md pl-7 pr-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 w-32 focus:outline-none focus:border-amber-500/30"
              />
            </div>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
            {candidates.length === 0 && (
              <p className="text-xs text-zinc-600 text-center py-6">No tracks match — all assigned or none uploaded.</p>
            )}
            {candidates.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-2 p-2 rounded-md bg-zinc-800/30 hover:bg-zinc-800/60 border border-white/5 cursor-pointer transition-colors"
                onClick={() => addTrack(t.id)}
                data-testid={`available-track-${t.id}`}
              >
                <div className="w-8 h-8 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
                  {t.image_url && <img src={t.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{t.title}</p>
                  <p className="text-[10px] text-zinc-500 capitalize truncate">{t.category}</p>
                </div>
                <Plus className="w-4 h-4 text-amber-400 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reward picker */}
      <div className="bg-zinc-900/40 border border-amber-500/15 rounded-xl p-4">
        <p className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80 mb-2">Reward track (unlocks at completion)</p>
        <select
          value={config.reward_track_id || ''}
          onChange={(e) => setConfig(c => ({ ...c, reward_track_id: e.target.value }))}
          className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-amber-500/40"
          data-testid="reward-track-select"
        >
          <option value="">— None —</option>
          {allTracks.map(t => (
            <option key={t.id} value={t.id}>{t.title}{t.category ? ` · ${t.category}` : ''}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
